import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../Header/Header";
import "../../assets/css/SubmitPage/SubmitPage.css";
import axios from "axios";
import { BASE_URL } from "../../store/config";

function SubmitPage() {
  const navigate = useNavigate();
  const { attemptId } = useParams();

  const [loading, setLoading] = useState(true);
  const [resultData, setResultData] = useState(null);
  const [finalMessage, setFinalMessage] = useState("");
  const [showReview, setShowReview] = useState(false);

  const passMessages = [
    "🎉 Great job! You’ve successfully cleared the assessment.",
    "🏆 Well done! You met the passing criteria.",
    "🚀 Excellent performance! Keep up the momentum.",
    "🌟 Impressive work! You’re on the right track.",
    "🔥 Brilliant! You’ve demonstrated strong understanding.",
  ];

  const failMessages = [
    "💪 Keep going! Every attempt makes you better.",
    "📘 Don’t worry — review your answers and try again.",
    "🌱 Almost there — a little more practice and you’ll succeed.",
    "⚡ Keep pushing forward — success is close.",
    "🎯 You’re improving! Try again to reach the goal.",
  ];

  useEffect(() => {
    const fetchResult = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}api/attempts/${attemptId}/result`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("access_token")}`,
            },
          }
        );

        const data = res.data;
        setResultData(data);

        const isPassed = data.score >= data.passing_score;

        const messages = isPassed ? passMessages : failMessages;

        const randomMsg =
          messages[Math.floor(Math.random() * messages.length)];

        setFinalMessage(randomMsg);
      } catch (err) {
        console.error("Result fetch error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchResult();
  }, [attemptId]);
  // =========================================
  // LOADING
  // =========================================
  if (loading) {
    return (
      <>
        <Header showProfile={true} />
        <div className="submit-container">
          <h2>Loading Result...</h2>
        </div>
      </>
    );
  }

  // =========================================
  // NO DATA
  // =========================================
  if (!resultData) {
    return (
      <>
        <Header showProfile={true} />
        <div className="submit-container">
          <h2>No Result Found</h2>
          <button onClick={() => navigate("/")}>Go Home</button>
        </div>
      </>
    );
  }

  // =========================================
  // EXTRACT DATA
  // =========================================
  const {
    assessment_title,
    score,
    total_score,
    percentage,
    passing_score,
    correct_answers,
    wrong_answers,
    skipped_answers,
    results,
  } = resultData;

  const isPassed = score >= passing_score;

  return (
    <>
      <Header showProfile={true} />

      <div className="result-page">
        {/* TOP */}
        <div className="result-top">
          <div className="trophy">
            <img src="/assets/images/trophy.png" alt="Trophy" />
          </div>

          <h1>{assessment_title}</h1>
          <h2>Assessment Completed</h2>
        </div>

        {/* SCORE CARD */}
        <div className="result-card">
          <div className="progress-ring">
            <svg width="180" height="180">
              <circle cx="90" cy="90" r="80" className="bg" />
              <circle
                cx="90"
                cy="90"
                r="80"
                className="progress"
                style={{
                  strokeDasharray: 502,
                  strokeDashoffset:
                    502 - (502 * percentage) / 100,
                }}
              />
            </svg>

            <div className="score-text">{percentage}%</div>
          </div>

          <h2>
            {score} / {total_score} Points
          </h2>

          {/* STATUS MESSAGE */}
          <div className={`badge ${isPassed ? "" : "low-score"}`}>
            {finalMessage}
          </div>

          {/* STATS */}
          <div className="stats">
            <div className="stat correct">
              <span>{correct_answers}</span>
              <p>Correct</p>
            </div>

            <div className="stat wrong">
              <span>{wrong_answers}</span>
              <p>Wrong</p>
            </div>

            <div className="stat skipped">
              <span>{skipped_answers}</span>
              <p>Skipped</p>
            </div>
          </div>

          {/* PROGRESS BAR */}
          <div className="progress-bar">
            <div style={{ width: `${percentage}%` }} />
          </div>

          {/* ACTIONS */}
          <div className="actions">
            <button
              className="review-btn"
              onClick={() => setShowReview(!showReview)}
            >
              {showReview ? "Hide Review" : "Show Review"}
            </button>

            {isPassed && (
              <button
                className="certificate-btn"
                onClick={() => {
                  localStorage.setItem(
                    "resultData",
                    JSON.stringify(resultData)
                  );
                  window.open("/certificate", "_blank", "noopener,noreferrer");
                }}
              >
                Get Certificate
              </button>
            )}

            <button
              className="feedback-btn"
              onClick={() =>
                window.open("/feedback", "_blank", "noopener,noreferrer")
              }
            >
              Feedback
            </button>

            <button
              className="home-btn"
              onClick={() =>
                window.open("/dashboard", "_blank", "noopener,noreferrer")
              }
            >
              Home
            </button>
          </div>
        </div>

        {/* REVIEW */}
        {showReview && (
          <div
            className="review-modal-overlay"
            onClick={() => setShowReview(false)}
          >
            <div
              className="review-modal"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="review-modal-header">
                <h2 style={{ color: "white" }}>Answer Review</h2>

                <button
                  className="close-btn"
                  onClick={() => setShowReview(false)}
                >
                  ✕
                </button>
              </div>

              <div className="review-modal-body">
                {results.map((result, index) => (
                  <div
                    key={index}
                    className={`review-card ${result.is_correct ? "correct-card" : "wrong-card"
                      }`}
                  >
                    <div className="q-row">
                      <span className="q-index">
                        Q{index + 1}.
                      </span>

                      <span
                        className="q-content"
                        dangerouslySetInnerHTML={{
                          __html: result.question,
                        }}
                      />
                    </div>

                    <p>
                      <strong>Your Answer:</strong>{" "}
                      {result.user_answer &&
                        result.user_answer.trim() !== ""
                        ? result.user_answer
                        : "Not Answered"}
                    </p>

                    <p>
                      <strong>Correct Answer:</strong>{" "}
                      {result.correct_answer}
                    </p>

                    <p>
                      {result.user_answer &&
                        result.user_answer.trim() !== ""
                        ? result.is_correct
                          ? "✔ Correct"
                          : "✖ Wrong"
                        : "⏭ Skipped"}
                    </p>

                    <p>
                      <strong>Score:</strong>{" "}
                      {result.score_awarded}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default SubmitPage;